import { useRoute, useRouter } from 'vue-router';
import { computed } from 'vue';
import { useQuasar } from 'quasar';
import { useLang } from '@/composables/lang';
import type { ITextValue, NotifyOptions } from './types';
import type { DateFormatLocale, FormatDateOptions } from '@/utils/dateUtil';
import {
  formatDate,
  formatDateTime,
  formatDistanceFromNow,
  getDateAutoFormatBy,
  getDateTimeAutoFormatBy,
} from '@/utils/dateUtil';
import {
  mdiCheckCircleOutline,
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiInformationOutline,
  mdiClose,
} from '@mdi/js';
import { Clipboard } from '@capacitor/clipboard';

export const useBase = () => {
  const { t, locale } = useLang();
  const { dark, loading, notify, dialog } = useQuasar();
  const route = useRoute();
  const router = useRouter();

  const isDark = computed(() => dark.isActive);
  const getCurrentPath = (fullPath = true) => {
    return fullPath ? route.fullPath : route.path;
  };
  const getPreviousPath = () => {
    const back = router.options.history.state.back;
    if (back) {
      return back;
    }
    return '/';
  };
  const getParam = (field: string): string | undefined => {
    if (!field) {
      return undefined;
    }
    return route.params ? (route.params[field] as string) : undefined;
  };
  const getParamNumber = (att: string): number => {
    const val = getParam(att);
    return val != undefined ? +val : 0;
  };
  const getQuery = (field: string): string | undefined => {
    if (!field) {
      return;
    }
    return route.query ? (route.query[field] as string) : undefined;
  };
  const getQueryNumber = (att: string): number => {
    const val = getQuery(att);
    return val != undefined ? +val : 0;
  };
  const onReplaceUrl = (url: string) => {
    history.pushState({}, '', url);
  };
  const appNavigateTo = (link: string | undefined, replace?: boolean): void => {
    if (!link) {
      return;
    }
    if (!replace) {
      router.push(link);
    } else {
      // window.location.replace(link);
      router.replace(link);
      // router.replace({ path: link });
    }
  };

  const appLoaderClose = () => {
    if (loading.isActive) {
      loading.hide();
    }
  };
  const appLoading = (open = true, message: string | undefined = undefined, delay = 0, spinnerColor = ''): void => {
    if (open) {
      appLoaderClose();
      loading.show({
        delay, // ms
        message: message || t('base.pleaseWait'),
        // Only override the spinner color when explicitly provided (keeps other loaders unchanged).
        ...(spinnerColor ? { spinnerColor } : {}),
      });
    } else {
      appLoaderClose();
    }
  };
  /* https://quasar.dev/quasar-plugins/notify
        position > top-left top-right bottom-left bottom-right top bottom left right center
    appToast('Quasar Framework Template',{type:'positive', position:'right', color:''});
    appToast('Quasar Framework Template',{caption:'5 Minutes ago', avatar: 'https://cdn.quasar.dev/img/boy-avatar.png'});
     */
  const appToast = (
    message: string,
    options: NotifyOptions | undefined = undefined,
  ) => {
    if (!message) {
      return;
    }

    let color;
    let textColor;
    if (options?.color == undefined && options?.type == undefined) {
      color = !dark.isActive ? 'white' : 'black';
      textColor = !dark.isActive ? 'black' : 'white';
    }
    let icon: string | undefined = '';
    if (options && options.type) {
      const t = options.type;
      if (t === 'positive') {
        icon = mdiCheckCircleOutline;
        textColor = 'white';
        color = 'positive';
      } else if (t === 'negative') {
        icon = mdiAlertOutline;
        textColor = 'white';
        color = 'negative';
      } else if (t === 'warning') {
        icon = mdiAlertCircleOutline;
        textColor = 'white';
        color = 'warning';
      } else if (t === 'info') {
        icon = mdiInformationOutline;
        textColor = 'white';
        color = 'info';
      }
    }
    notify(
      Object.assign(
        {
          message,
          icon,
          timeout: 5000,
          progress: true,
          position: 'top-right',
          multiLine: false,
          color: color || '',
          textColor: textColor || '',
          actions: !options?.hideClose
            ? [
                {
                  icon: mdiClose,
                  round: true,
                  dense: true,
                  color: textColor || 'white',
                },
              ]
            : [],
        },
        options,
      ),
    );
  };

  /**
   * const conf = await appConfirm(t('app.monogram'), t('base.deleteConfirm'));
   */
  const appConfirm = async (
    title: string,
    text: string,
    okBtn = {}, // btn component
    cancelBtn = {}, // btn component
  ) => {
    return new Promise((resolve) => {
      dialog({
        title,
        message: text,
        ok: Object.assign(
          { textColor: 'primary', flat: true, label: t('okay') },
          okBtn,
        ),
        // ok: okText===undefined ? tc('okay') : okText,
        cancel: Object.assign(
          { textColor: 'grey', flat: true, label: t('cancel') },
          cancelBtn,
        ),
        persistent: true,
      })
        .onOk(() => {
          resolve(true);
        })
        .onOk(() => {
          resolve(true);
        })
        .onCancel(() => {
          resolve(false);
        })
        .onDismiss(() => {
          resolve(false);
        });
    });
  };
  /**
   * Acknowledge-only dialog (single OK, no cancel) — the same Quasar `dialog` facade as
   * `appConfirm`, for surfacing a blocking message the operator must read + dismiss (e.g.
   * "This customer is already checked in today"). Resolves when the dialog is closed.
   */
  const appAlert = async (title: string, message: string, okBtn = {}): Promise<void> => {
    return new Promise((resolve) => {
      dialog({
        title,
        message,
        ok: Object.assign(
          { textColor: 'primary', flat: true, label: t('okay') },
          okBtn,
        ),
        persistent: true,
      })
        .onOk(() => resolve())
        .onDismiss(() => resolve());
    });
  };

  const searchOperations: ITextValue[] = [
    // { text: t('base.match'), value: SearchOperation.MATCH },
    // { text: t('base.equa'), value: SearchOperation.EQUA },
    // { text: t('base.notEqua'), value: SearchOperation.NOT_EQUA },
    // { text: t('base.greaterThan'), value: SearchOperation.GREATER_THAN },
    // {
    //   text: t('base.greaterThanEqua'),
    //   value: SearchOperation.GREATER_THAN_EQUA,
    // },
    // { text: t('base.lessThan'), value: SearchOperation.LESS_THAN },
    // { text: t('base.lessThanEqua'), value: SearchOperation.LESS_THAN_EQUA },
  ];

  // const datePickerLocale = {
  //   /* starting with Sunday */
  //   days: t('date.days').split('_'),
  //   daysShort: t('date.dayShorts').split('_'),
  //   months: t('date.months').split('_'),
  //   monthsShort: t('date.monthShorts').split('_'),
  //   firstDayOfWeek: 1,
  // };
  const datePickerLocale = computed(() =>
    locale.value
      ? {
          days: t('date.days').split('_'),
          daysShort: t('date.dayShorts').split('_'),
          months: t('date.months').split('_'),
          monthsShort: t('date.monthShorts').split('_'),
          firstDayOfWeek: 1,
        }
      : undefined,
  );

  const KNOWN_LOCALES: readonly DateFormatLocale[] = ['en', 'es', 'vi', 'zh'];
  const resolveLocale = (): DateFormatLocale => {
    const l = locale.value;
    return (KNOWN_LOCALES as readonly string[]).includes(l) ? (l as DateFormatLocale) : 'en';
  };

  function appFormatDate(d: string | Date | undefined, opts?: FormatDateOptions): string;
  function appFormatDate(d: string | undefined, fmt: string): string | undefined;
  function appFormatDate(
    d: string | Date | undefined,
    optsOrFmt?: FormatDateOptions | string,
  ): string | undefined {
    if (typeof optsOrFmt === 'string') {
      return formatDate(typeof d === 'string' ? d : d?.toISOString(), optsOrFmt, locale.value);
    }
    return formatDate(d, { locale: resolveLocale(), ...(optsOrFmt ?? {}) });
  }
  const appFormatDateTime = (d: string, fmt: string) => {
    return formatDateTime(d, fmt, locale.value);
  };
  const appFormatDateDistance = (d: string) => {
    return formatDistanceFromNow(d, locale.value);
  };
  const appFormatDateAuto = (d: string) => {
    return d ? getDateAutoFormatBy(d, locale.value) : '';
  };
  const appFormatDateTimeAuto = (d: string) => {
    return d ? getDateTimeAutoFormatBy(d, locale.value) : '';
  };

  /**
   * <div ref="bottomSection"></div>
   * scrollToTop(bottomSection.value);
   * @param el
   */
  const scrollToTop = (el: Element) => {
    // window.scrollTo(0, 0);
    if (el) {
      // el.scrollIntoView({ behavior: 'smooth' });
      // el.scrollIntoView({ block: 'end', behavior: 'smooth' });
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const isDevMode = () => {
    return process.env.NODE_ENV == 'development';
  };
  const inputSanitizeHtml = async (str: string) => {
    if (!str) {
      return '';
    }
    // Lazy-load dompurify: chỉ tải khi thật sự sanitize, để lib rời eager chunk của useBase
    // (composable dùng khắp app) thay vì bị kéo vào qua boot.
    const { default: DOMPurify } = await import('dompurify');
    return DOMPurify.sanitize(str, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a'],
      ALLOWED_ATTR: ['href', 'class'],
    });
  };
  const readableNumber = (num: number, digits: number) => {
    if (num < 1000) {
      return num;
    }
    const lookup = [
      { value: 1, symbol: '' },
      { value: 1e3, symbol: 'k' },
    ];
    const rx = /\.0+$|(\.\d*[1-9])0+$/;
    const item = lookup
      .slice()
      .reverse()
      .find((item) => {
        return num >= item.value;
      });
    return item
      ? (num / item.value).toFixed(digits).replace(rx, '$1') +
          (item.symbol ? t('readableNum.' + item.symbol) : '')
      : '0';
  };
  const writeToClipboard = async (text: string) => {
    await Clipboard.write({
      string: text,
    });
    appToast(t('success.copy'), { multiLine: false });
    return new Promise((resolve) => {
      resolve(true);
    });
  };
  return {
    router,
    getParam,
    getQuery,
    appNavigateTo,
    appLoading,
    appToast,
    appConfirm,
    appAlert,
    getCurrentPath,
    searchOperations,
    datePickerLocale,
    getParamNumber,
    getQueryNumber,
    getPreviousPath,
    appFormatDate,
    scrollToTop,
    onReplaceUrl,
    isDevMode,
    inputSanitizeHtml,
    appFormatDateTime,
    appFormatDateDistance,
    readableNumber,
    isDark,
    writeToClipboard,
    appFormatDateAuto,
    appFormatDateTimeAuto,
  };
};
